import type { EntityViewType } from "@config";
import { useViewContext } from "@editor-providers";
import { cn } from "@lib/cn";
import { useIsMobile } from "@library/shared";
import { useMemo } from "react";
import { CarouselCtxProvider } from "../ctx";
import { useCarouselSwiper } from "../hooks";
import "../styles/carousel.scss";
import {
  CarouselContainerEnum,
  CarouselStyleEnum,
  type ICarouselEditorData,
  type ICarouselSets,
} from "../types";
import { ensureCarouselComboSettings } from "../utils";
import { CarouselBgImages } from "./carousel-bg/carousel-bg-images";
import { CarouselItems } from "./carousel-items/carousel-items";
import { CarouselNavBtn } from "./carousel-nav-btn";
import { CarouselPagination } from "./carousel-pagination";

interface IProps {
  entity: EntityViewType;
  editorData?: ICarouselEditorData;
  sets: ICarouselSets;
}

export const CarouselView = ({ sets, entity, editorData }: IProps) => {
  const { view } = useViewContext();
  const isEditor = view === "editor";
  const isMobile = useIsMobile();

  const comboSettings = useMemo(() => {
    return ensureCarouselComboSettings({
      comboSettings: entity.settings,
      isMobile,
    });
  }, [entity.settings, isMobile]);

  const isSideBySide =
    comboSettings.carouselContainer === CarouselContainerEnum.SideBySide;
  const isFullScreen =
    comboSettings.carouselStyle === CarouselStyleEnum.FullScreen;

  const {
    bgImagesRef,
    itemsRef,
    nextBtnRef,
    prevBtnRef,
    paginationRef,

    handleSwapNext,
    handleSwapPrev,
  } = useCarouselSwiper({
    comboSettings,
    sets,
    isFullScreen,
    editorData,
    isEditor,
    isMobile,
  });

  return (
    <CarouselCtxProvider
      ctxState={{
        comboId: entity.id,
        comboLayers: entity.layers,
        sets,
        handleSwapNext,
        handleSwapPrev,
        isFullScreen,
        isSideBySide,
        comboSettings,
        isEditor,
        editorData,
      }}
    >
      <div
        className={cn({
          "carousel-combo tw-relative tw-flex tw-w-full tw-flex-col": true,
          // 100dvh on mobile so the slide tracks the *visible* viewport
          // (mobile browser URL bars collapse vh's reference height); md+
          // keeps 100vh since desktop has no dynamic chrome.
          "tw-min-h-[100dvh] md:tw-min-h-screen": isFullScreen,
        })}
        data-carousel-container={comboSettings.carouselContainer}
        data-carousel-animation={comboSettings.carouselAnimationType}
      >
        {isMobile ? (
          <>
            <div className="tw-relative tw-flex tw-h-auto tw-w-full tw-flex-1">
              <CarouselItems ref={itemsRef} />
            </div>
            <div className="carousel-nav-row tw-relative tw-flex tw-w-full tw-items-center tw-justify-between">
              <CarouselNavBtn type="prev" ref={prevBtnRef} />
              <CarouselPagination ref={paginationRef} inline />
              <CarouselNavBtn type="next" ref={nextBtnRef} />
            </div>
          </>
        ) : (
          <>
            <CarouselNavBtn type="prev" ref={prevBtnRef} />
            <div className="tw-relative tw-flex tw-h-auto tw-w-full">
              <CarouselItems ref={itemsRef} />
              <CarouselPagination ref={paginationRef} />
            </div>
            <CarouselNavBtn type="next" ref={nextBtnRef} />
          </>
        )}

        {isFullScreen && <CarouselBgImages ref={bgImagesRef} />}
      </div>
    </CarouselCtxProvider>
  );
};
